import React, { useCallback, useEffect, useRef } from 'react';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { useEventListener } from '@Hooks';
import { dispatchTargetedEvent } from '@Map/utils';
import { FlyTo, GeocoderResult } from '../../types';
import {
	RESTRICT_SEARCH,
	SEARCH_CHANGED,
	SEARCH_FLYTO,
	SET_DEFAULT_SEARCH,
	STYLE_SEARCH,
} from '../../constants';

export interface EsriGeocoderProps {
	/** id used to link the geocoder with the ESRI map */
	id: string;
	/** event fired when a search result is selected */
	onSearchChange?: (result: GeocoderResult | null) => void;
	/** default search term to populate on mount */
	defaultSearch?: string;
	/** input label */
	label?: string;
	/** Fly to search options: always, never, onSelect, defaultSearch
	 * @default FlyTo.always
	 */
	flyTo?: FlyTo;
	/** restrict search results to current map bounds
	 * @default true
	 */
	restrictToBounds?: boolean;
	/** props to pass through to inner TextField component */
	TextFieldProps?: MuiTextFieldProps;
}

/**
 * Standalone ESRI geocoder search that can be mounted anywhere in the app
 * (e.g. a header or sidebar) and remain wired to the ESRI map via `id` +
 * `geocoderId` on `ReactEsriMap`.
 */
const EsriGeocoder = ({
	id,
	onSearchChange,
	defaultSearch,
	label,
	flyTo,
	restrictToBounds,
	TextFieldProps = {},
}: EsriGeocoderProps): JSX.Element => {
	const geocoder = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dispatchTargetedEvent(geocoder.current, SEARCH_FLYTO, flyTo);
	}, [flyTo]);

	useEffect(() => {
		dispatchTargetedEvent(geocoder.current, RESTRICT_SEARCH, restrictToBounds);
	}, [restrictToBounds]);

	useEffect(() => {
		if (!geocoder.current) return;
		// Write to a data attribute so the map can read it even if its listener
		// wasn't set up yet when this effect first fired (sibling timing).
		if (defaultSearch) {
			geocoder.current.dataset.defaultSearch = defaultSearch;
		} else {
			delete geocoder.current.dataset.defaultSearch;
		}
		dispatchTargetedEvent(geocoder.current, SET_DEFAULT_SEARCH, defaultSearch);
	}, [defaultSearch]);

	useEffect(() => {
		dispatchTargetedEvent(geocoder.current, STYLE_SEARCH, {
			...TextFieldProps,
			label,
		});
	}, [TextFieldProps, label]);

	const searchChanged = useCallback(
		(e: Event) => {
			const result = (e as CustomEvent<GeocoderResult | null>).detail;
			onSearchChange?.(result);
		},
		[onSearchChange],
	);

	useEventListener(SEARCH_CHANGED, searchChanged, geocoder);

	return <div id={id} ref={geocoder} data-cy="esri-geocoder" />;
};

export default EsriGeocoder;
